import React from 'react';

import { Select } from '@aline/common';
import { alpha, Typography } from '@mui/material';
import { useAccessGuard } from 'src/components/auth';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { useGetDocumentHeader } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { createGeneratePlaybookPrompt } from './generate-playbook-prompt';
import { useDraftPromptInChat } from './use-draft-prompt-in-chat';

const useStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  importText: {
    fontWeight: 600,
  },
  root: {
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    alignItems: 'center',
    background: `linear-gradient(130deg, ${alpha(theme.appColors.primary, 0.08)} 0%, ${alpha(
      theme.appColors.accent1,
      0.08
    )} 100.06%)`,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    cursor: 'pointer',
    display: 'flex',
    padding: theme.spacing(1.25),
  },
  subText: {
    fontSize: 12,
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

/**
 * Hands playbook generation to the AI chat rather than running it from here.
 *
 * This card used to open a dialog asking for the party and some guidance and
 * start the job on its own. The chat's playbook agent asks the same questions,
 * shows a plan for approval first, and then tracks the run in a card, and
 * there is no reason for a second way in that skips all of that. Clicking now
 * opens the chat drawer with the document mentioned and the request written,
 * so the user only has to press send and the rest is the chat-page flow.
 */
export const AutoGeneratePlaybook = React.memo(function AutoGeneratePlaybook(): JSX.Element {
  const { classes } = useStyles();
  const { documentId } = useDocumentParams();
  const { document } = useGetDocumentHeader(documentId);
  const draftPromptInChat = useDraftPromptInChat();
  const handleOpenGenerate = useAccessGuard({
    callback: () =>
      draftPromptInChat(
        createGeneratePlaybookPrompt({ documentId, documentName: document?.name ?? '' })
      ),
    minRole: Apollo.OrgRole.Basic,
  });

  return (
    <div
      className={classes.root}
      data-cy={Select.document.editor.rightDrawer.ai.panel.autoGenerateButton}
      onClick={handleOpenGenerate}
    >
      <div className={classes.content}>
        <div className={classes.title}>
          <SmallAiSparkleIcon />
          <Typography className={classes.importText}>Generate a playbook</Typography>
        </div>
        <Typography className={classes.subText} color="textSecondary">
          Ask Aline AI to build one from this document
        </Typography>
      </div>
    </div>
  );
});
